"use client";

import React, { useEffect, useRef, useState } from "react";
const steps = [{
  id: "medical-loss",
  title: "First Class Medical Loss",
  description: "The transition from PIC to right-seat operations begins here. We help you navigate the regulatory shift while maintaining your connection to the cockpit through our pilot-pairing network.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/medical_cessation_cockpit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/medical_cessation_cockpit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/medical_cessation_cockpit.png-wvc-sizes",
    alt: "Close up of aircraft instrumentation panel"
  }
}, {
  id: "cognitive-shift",
  title: "Cognitive Shift Detection",
  description: "Subtle changes in checklist discipline or situational awareness are early warning signs. Our Care Advisors implement structured support before safety margins are compromised.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/pilot_checklist_precision.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/pilot_checklist_precision.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/pilot_checklist_precision.png-wvc-sizes",
    alt: "Pilot performing a pre-flight checklist"
  }
}, {
  id: "asset-transition",
  title: "Aircraft Equity Extraction",
  description: "When the airframe becomes a liability rather than an asset, we structure a transition that funds your lifetime care while potentially keeping the aircraft available for your use.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/hangar_sunset_transition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/hangar_sunset_transition.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/hangar_sunset_transition.png-wvc-sizes",
    alt: "Private aircraft in a hangar at sunset"
  }
}, {
  id: "solo-ager-status",
  title: "Solo Ager Flight Plan",
  description: "For pilots without immediate family nearby, we serve as your ground crew. We manage the logistics of aging in place, ensuring your 'flight plan' is executed with precision.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_dawn_horizon.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_dawn_horizon.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_dawn_horizon.png-wvc-sizes",
    alt: "Runway lights at dawn looking toward the horizon"
  }
}, {
  id: "final-approach",
  title: "The Final Approach",
  description: "Full-time in-home care delivery. Your home remains your base, supported by a dedicated team that understands the discipline and dignity of a lifetime in aviation.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/aviation_home_office.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/aviation_home_office.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/aviation_home_office.png-wvc-sizes",
    alt: "Aviation-themed home office with flight memorabilia"
  }
}];
export default function CessationTriggers() {
  const [activeIndex, setActiveIndex] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const observers = steps.map((_, index) => {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setActiveIndex(index);
        }
      }, {
        rootMargin: "-40% 0px -40% 0px",
        threshold: 0
      });
      const currentRef = stepRefs.current[index];
      if (currentRef) {
        observer.observe(currentRef);
      }
      return observer;
    });
    return () => {
      observers.forEach(obs => obs.disconnect());
    };
  }, []);
  return <section data-section-id="264881" id="cessation-triggers" className="bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div className="max-w-2xl mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-6 tracking-tight uppercase">
            Cessation Triggers
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Every pilot needs a flight plan for the transition from the cockpit to the home. Identify the waypoints that signal it is time to adjust your heading.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 relative">
          
          {}
          <div className="hidden lg:block relative h-[600px]">
            <div className="sticky top-32 w-full h-[600px] rounded-md overflow-hidden shadow-2xl bg-muted border border-secondary">
              {steps.map((step, i) => <img key={step.id} data-index={i} src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading={i === 0 ? "eager" : "lazy"} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out grayscale-[0.3] ${activeIndex === i ? "opacity-100 z-10" : "opacity-0 z-0"}`} />)}
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent z-20 pointer-events-none" />
            </div>
          </div>

          {}
          <div className="relative pb-32">
            {}
            <div className="absolute left-[11px] md:left-[15px] top-4 bottom-0 w-px bg-border" />

            <div className="flex flex-col gap-16 md:gap-32">
              {steps.map((step, i) => {
              const isActive = activeIndex === i;
              return <div key={step.id} data-index={i} ref={el => {
                stepRefs.current[i] = el;
              }} className="relative pl-10 md:pl-16 transition-all duration-500">
                    {}
                    <div className={`absolute left-0 md:left-1 top-2 h-6 w-6 rounded-md border-2 transition-all duration-500 flex items-center justify-center bg-background ${isActive ? "border-primary scale-110 shadow-lg" : "border-muted-foreground/30 scale-100"}`}>
                      <div className={`h-2 w-2 rounded-md transition-colors duration-500 ${isActive ? "bg-primary" : "bg-transparent"}`} />
                    </div>

                    {}
                    <div className="lg:hidden mb-6 rounded-md overflow-hidden shadow-md bg-muted aspect-[4/3] border border-secondary">
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" className="w-full h-full object-cover grayscale-[0.3]" />
                    </div>

                    {}
                    <div className={`transition-opacity duration-500 ${isActive ? "opacity-100" : "opacity-40 hover:opacity-70"}`}>
                      <span className="text-sm font-mono font-semibold tracking-widest uppercase text-primary mb-2 block">
                        Waypoint 0{i + 1}
                      </span>
                      <h3 className={`text-2xl md:text-3xl font-default font-bold mb-4 transition-colors duration-500 ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                        {step.title}
                      </h3>
                      <p className="text-base md:text-lg font-default leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}